import * as React from 'react';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';

import { ISystem } from '../../api/models/ISystem';
import { SystemsContext, TTab } from './ISystemsContext';
import { SystemList } from './SystemList';
import { SystemApi } from '../../api/services/SystemApi';
import { TResourceState } from '../../types/TResourceState';
import { TMode } from '../SystemPane/ISystemsContext';
import { ViewSystem } from './ViewSystem/ViewSystem';
import { EditSystem } from './EditSystem/EditSystem';
import { AddSystem } from './AddSystem/AddSystem';
import { useGlobalState } from '../../contexts/state/GlobalStateContext'; 
import { IDriverset } from '../../api/models';

const SystemsManager = () => {
  const globalState = useGlobalState();

  const toastProvider = useToast();

  const [mode, setMode] = React.useState<TMode>('list');

  // System proxies loaded from back end:
  const [systems, setSystems] = React.useState<ISystem[]>([]);
  const [systemsReady, setSystemsReady] = React.useState<TResourceState>('loading');

  // System selected in list:
  const [system, setSystem] = React.useState<ISystem | null>(null);
  const [systemReady, setSystemReady] = React.useState<TResourceState>('ready');

  const [driverset, setDriverset] = React.useState<IDriverset | null>(null);

  const [tab, setTab] = React.useState<TTab>('info');

  const parseError = (error: any): string => {
    if(error?.code == 'ERR_CANCELED') return "Server timeout."
    const status = error?.response?.status;
    switch(status) {
      case 403: return "This operation is not permitted.";
      case 409: return "A system with this name already exists.";
      case 422: return "The form content could not be validated.";
      default: return "Something went wrong while creating the system. Please try again.";
    }
  }  
  
  React.useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = () => {
    setSystems([]);
    setSystemsReady('loading');
    SystemApi.list()
    .then((res) => {
      setSystems(res);
      setSystemsReady('ready');
    })
    .catch(() => {
      setSystemsReady('failed');
    });
  }

  const loadSystem = async (system: ISystem): Promise<ISystem | null> => {
    setSystemReady('loading');
    try {
      const res = { ...await SystemApi.get(system.uuid), hydrated: true };
      setSystemReady('ready');
      setSystem(res);
      return res;
    } catch {
      setSystemReady('ready');
      return null;
    }
  }

  const parseSystem = async () => {
    if (!system) return;
    const res = await loadSystem(system);
    if(!res) {
      toastProvider.toast("Failed to load system.", { dismissable: true });
      return;
    }
    globalState.setProject({ 
      ...globalState.project, 
      systemUUID: res.uuid,
      systemName: res.name
    });
    globalState.parseLayers(res.layers || []);
    globalState.setDriversets(res.driversets || []);
  }

  const createSystem = async (newSystem: ISystem, signal: AbortSignal): Promise<boolean> => {
    await SystemApi.create(newSystem, signal)
    .then((res) => {
      setSystem(res);
      setSystems([...systems, res]);
      setMode('view');
      toastProvider.toast("System created successfully.", { dismissable: true });
    })
    .catch(error => {
      toastProvider.toast(parseError(error), { dismissable: true });
      return false;
    });
    return true;
  }

  const updateSystem = async (editSystem: ISystem, signal: AbortSignal): Promise<boolean> => {
    await SystemApi.update(editSystem.uuid, editSystem, signal)
    .then((data) => {
      setSystem(data);
      setSystems([ ...systems.filter(s => s.uuid != editSystem.uuid), data ]);
      setMode('view');
      toastProvider.toast("System updated successfully.", { dismissable: true });
    })
    .catch(error => {
      toastProvider.toast(parseError(error), { dismissable: true });
      return false;
    });
    return true;
  }

  const deleteSystem = () => {
    if (!system) return;
    setSystemReady('loading');
    SystemApi.delete(system.uuid)
    .then((res) => {
      setSystem(null);
      setSystems(systems.filter(s => s.uuid != res.uuid));
      setSystemReady('ready');
      setMode('list');
      toastProvider.toast("System deleted successfully.", { dismissable: true });
    })
    .catch(err => {
      setSystemReady('failed');
      toastProvider.toast("The system could not be deleted.", { dismissable: true });     
    });
  }  

  return (
    <SystemsContext.Provider value={{
      mode, setMode,
      tab, setTab,
      systems, loadSystems, systemsReady, setSystemsReady,
      system, setSystem, loadSystem, systemReady, setSystemReady,
      driverset, setDriverset,
      parseSystem,
      createSystem,
      updateSystem,
      deleteSystem,
    }}>
      {mode == 'list' && <SystemList/>}
      {mode == 'view' && <ViewSystem/>}
      {mode == 'add' && <AddSystem/>}
      {mode == 'edit' && <EditSystem/>}
    </SystemsContext.Provider>
  )
}

export { SystemsManager }
